import React, { Component, type ComponentType, type ReactNode } from 'react';

import PropTypes from 'prop-types';

import { type MaybeChoreographedComponentProps } from '@atlassian/ipm-choreographer';

import {
	type CurrentMessage,
	type ComponentType as EngagementComponentType,
} from '../../common/types/message';
import { type State as EngagementStoreState } from '../engagement-store';

type State = {
	message: CurrentMessage | null;
};

export type Props = {
	engagementId?: string;
	children?: ReactNode;
} & MaybeChoreographedComponentProps;

export default (
	InnerComponent: ComponentType<any>,
	componentType: EngagementComponentType,
): ComponentType<any> => {
	function isTargetComponent(message: CurrentMessage): boolean {
		return message.componentType === componentType;
	}

	function computeNewState(state: EngagementStoreState): State {
		const current = state.current;
		if (current && isTargetComponent(current)) {
			let message;
			switch (current.componentType) {
				case 'contextualSurvey':
					message = {
						...current,
						aaid: state.config.aaid,
						cloudId: state.config.cloudId,
						stargateUrl: state.config.stargateUrl,
						product: state.config.product,
						orgId: state.config.orgId,
						workspaceId: state.config.workspaceId,
					};
					break;

				default:
					message = current;
			}

			return {
				message,
			};
		}

		return { message: null };
	}

	function getComponentType(): EngagementComponentType {
		return componentType;
	}

	// eslint-disable-next-line @repo/internal/react/no-class-components
	class StoreWrapper extends Component<Props, State> {
		context: any;
		static contextTypes = {
			subscribeEngagementState: PropTypes.func,
			setComponentConfig: PropTypes.func,
		};

		readonly state: Readonly<State> = {
			message: null,
		};

		UNSAFE_componentWillMount() {
			if (this.canSubscribe()) {
				this.unsubscribe = this.subscribeToStore();
			}
			if (this.canSetConfig()) {
				this.removeConfig = this.setChoreographerConfig();
			}
		}

		UNSAFE_componentWillReceiveProps(nextProps: Props) {
			if (
				this.props?.engagementId !== nextProps?.engagementId &&
				this.removeConfig &&
				this.canSetConfig()
			) {
				this.removeConfig();
				this.removeConfig = this.setChoreographerConfig(nextProps);
			} else if (
				(this.props?.messageType !== nextProps?.messageType ||
					this.props?.onMessageDisposition !== nextProps?.onMessageDisposition) &&
				this.canSetConfig()
			) {
				this.setChoreographerConfig(nextProps);
			}
		}

		componentWillUnmount() {
			if (this.unsubscribe) {
				this.unsubscribe();
			}
			if (this.removeConfig) {
				this.removeConfig();
			}
		}

		onStoreChange = (state: EngagementStoreState) => {
			this.setState(computeNewState(state));
		};

		canSetConfig(): boolean {
			return this.context && this.context.setComponentConfig;
		}

		setChoreographerConfig(props: Props = this.props) {
			return this.context.setComponentConfig(
				getComponentType(),
				{
					messageType: props.messageType,
					onMessageDisposition: props.onMessageDisposition,
				},
				props.engagementId,
			);
		}

		removeConfig!: () => void;

		unsubscribe!: () => void;

		canSubscribe(): boolean {
			return this.context && this.context.subscribeEngagementState;
		}

		subscribeToStore() {
			return this.context.subscribeEngagementState(this.onStoreChange);
		}

		render() {
			const { children, engagementId } = this.props;
			const { message } = this.state;

			if (!message) {
				if (componentType === 'inlineDialog') {
					return children || null;
				}

				return null;
			}

			if (engagementId && message !== null && message.target && engagementId !== message.target) {
				return null;
			}

			const messageKey = `${message.messageId}.${message.variationId}.${message.componentId}`;

			// Key is useful here to make sure that when the currently active
			// messages changes, the old component is unmounted (therefore
			// firing an analytic event for hiding the component) and mounting
			// a new component for the new message (firing a shown event for
			// the new message). Not using key would allow React to apply an
			// optimization that would reuse the current component for a new
			// message which would not call the normal mount and unmount
			// lifecycle methods.
			return <InnerComponent key={messageKey} {...this.props} {...message} />;
		}
	}

	return StoreWrapper;
};
